import {
  call,
  delay,
  fork,
  put,
  race,
  select,
  take,
  takeLatest,
} from 'typed-redux-saga/macro';

import {
  SAVE_ALL_DOCUMENTS,
  SAVE_ALL_SETTLED,
  SAVE_FAILURE,
  SAVE_NOW,
  SAVE_REQUEST,
  SAVE_SUCCESS,
} from '../actions';
import { TDOId } from '@common-modules/universal/models/Brands';
import {
  serviceDeleteAsset,
  serviceListDocumentRedactionAssets,
  serviceSaveDocumentRedactionData,
} from '../services';
import { redactionsToAssetBody } from '../helpers';
import { hasUnsavedWork, SessionState } from '../models';
import { selectSession, selectSuspendedSessions } from '../selectors';
import {
  AUTO_SAVE_INTERVAL_MS,
  DOCUMENT_REDACTION_ASSET_HISTORY_LIMIT,
} from '../store';
import { GraphQLConfig, selectToken, selectGraphQLConfig } from './auth';

/**
 * Best-effort cleanup of older `document-redaction-data` assets after a
 * successful save. Keeps the most-recent N (per `DOCUMENT_REDACTION_ASSET_HISTORY_LIMIT`)
 * and deletes the rest. Errors are logged but never bubble up — the user-visible
 * save state is `SUCCESS` regardless of rotation success.
 *
 * Pattern mirrors `imageRedactionSaga`'s post-save cleanup.
 */
function* rotateRedactionAssets(
  tdoId: string,
  token: string,
  cfg: GraphQLConfig
) {
  try {
    const all = yield* call(
      serviceListDocumentRedactionAssets,
      tdoId,
      token,
      cfg.apiRoot,
      cfg.graphQLEndpoint,
      cfg.veritoneAppId
    );
    // List is newest-first (orderDirection: desc on createdDateTime). Anything
    // past the keep-N cutoff is eligible for deletion.
    const toDelete = all.slice(DOCUMENT_REDACTION_ASSET_HISTORY_LIMIT);
    for (const asset of toDelete) {
      yield* call(
        serviceDeleteAsset,
        asset.id,
        token,
        cfg.apiRoot,
        cfg.graphQLEndpoint,
        cfg.veritoneAppId
      );
    }
  } catch (e) {
    console.warn('Document redaction asset rotation failed (non-fatal):', e);
  }
}

export interface PersistSessionOptions {
  /**
   * Round-tripped on SAVE_REQUEST / SAVE_SUCCESS / SAVE_FAILURE so a caller can
   * tell the save it triggered from an interval save.
   */
  requestId?: string;
  /** VE-26422 burn-in label — see autoSaveSaga for how it is chosen. */
  expectedRedactedVersion?: number;
}

export type PersistSessionResult =
  | { ok: true; savedAt: string }
  | { ok: false; message: string };

/**
 * Persist ONE snapshot of a session as a `document-redaction-data` asset,
 * wrapped in the SAVE_REQUEST → SAVE_SUCCESS / SAVE_FAILURE lifecycle.
 *
 * Takes the session as an argument rather than reading `selectSession`, so the
 * caller decides what gets saved: the autosave loop passes the live session,
 * and the export passes the snapshot it is about to burn in. That snapshot may
 * already be closed in the store — VE-28061: the user can leave the editor
 * while Redact File runs, and the burn-in must still persist exactly what it
 * renders, carrying the burn-in label the staleness verdict depends on. The
 * lifecycle actions carry the snapshot's own `tdoId` and are applied only to a
 * session showing that document, so a detached save cannot touch whatever
 * document is open now.
 *
 * Returns the outcome as a value as well, for callers that sequence on it.
 */
export function* persistSessionSnapshot(
  session: SessionState,
  { requestId, expectedRedactedVersion }: PersistSessionOptions
): Generator<any, PersistSessionResult, any> {
  // Capture the rev of the snapshot being persisted, so SAVE_SUCCESS can
  // detect an edit that dirtied the session mid-save.
  const savedRev = session.save.rev;
  const { tdoId } = session;
  yield* put(SAVE_REQUEST({ tdoId, requestId }));

  try {
    const token: string = yield* select(selectToken);
    const cfg = yield* select(selectGraphQLConfig);
    // Named fields, so a forgotten one is visible here rather than silently
    // absent from the saved asset — the VE-28061 failure mode, which cost every
    // applied watermark when this helper was lifted out of the autosave loop.
    // `appliedLexicons` is in that same category: it is what `watchFirstOpen`
    // reads to decide a document has already been evaluated, so dropping it
    // makes every reopen re-run a full apply and autosave a new version with no
    // user action (review-gate B5).
    const body = redactionsToAssetBody({
      redactions: session.redactions,
      tdoId: session.tdoId,
      excludedPages: session.excludedPages,
      pageRedacts: session.pageRedacts,
      detections: session.detections,
      watermarks: session.watermarks,
      watermarksOff: session.watermarksOff,
      batesStamp: session.batesStamp,
      batesStampOff: session.batesStampOff,
      appliedLexicons: session.appliedLexicons,
    });
    const result = yield* call(
      serviceSaveDocumentRedactionData,
      session.tdoId,
      body,
      token,
      cfg.apiRoot,
      cfg.graphQLEndpoint,
      expectedRedactedVersion
    );
    yield* put(
      SAVE_SUCCESS({
        tdoId,
        savedAt: result.createdDateTime,
        requestId,
        savedRev,
        expectedRedactedVersion,
      })
    );
    // Rotation is a background concern — fork it so the saga returns to
    // the race loop immediately and remains responsive to the next
    // SAVE_NOW even if there are many historical assets to delete.
    yield* fork(rotateRedactionAssets, session.tdoId, token, cfg);
    return { ok: true as const, savedAt: result.createdDateTime };
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : 'Save failed';
    yield* put(SAVE_FAILURE({ tdoId, code: 'SAVE_ERROR', message, requestId }));
    return { ok: false as const, message };
  }
}

export function* autoSaveSaga() {
  // App-lifetime loop: forked once at startup and lives across every document
  // open/close. Do NOT race against CLOSE_DOCUMENT here — closing one doc must
  // not kill the saga or future docs won't get auto-saved. The `!session`
  // guard below handles the "no doc open" case naturally.
  while (true) {
    const { manual } = yield* race({
      manual: take(SAVE_NOW),
      interval: delay(AUTO_SAVE_INTERVAL_MS),
    });

    const session = yield* select(selectSession);
    if (!session) {
      continue;
    }

    // The auto-save interval skips clean sessions to avoid no-op network
    // traffic. A user-triggered SAVE_NOW always runs — clicking the button
    // should produce a visible save lifecycle (request -> success) even if
    // nothing has changed since the last save.
    //
    // VE-25718: `error` is included, so a save that failed is retried on the
    // next tick instead of stranding the edits it was carrying. Without this
    // the interval skips them forever — `status` only leaves `error` when the
    // user happens to make another edit.
    if (!manual && !hasUnsavedWork(session.save)) {
      continue;
    }
    // Editability is enforced at the UI layer (canEdit = saveEditFiles, which
    // the real TDO lock drops to view-only for non-holders). A view-only user
    // can't dirty the session or trigger a manual save, so no explicit gate
    // is needed here.

    // Round-trip the manual SAVE_NOW's requestId (if present) on
    // SAVE_REQUEST / SAVE_SUCCESS / SAVE_FAILURE so callers that race on
    // completion can match on the specific save they triggered.
    const requestId = manual?.payload?.requestId;
    /**
     * VE-26422: a SAVE_NOW may carry the version a burn-in is about to
     * produce, which is what marks the asset as sent for burn-in. (The export
     * itself now saves through persistSessionSnapshot directly — VE-28061 — so
     * in practice this arrives only from callers that still go via SAVE_NOW.)
     *
     * A manual save of a CLEAN session carries the previous asset's label
     * forward instead of writing "never sent". Clicking Save without changing
     * anything always writes an asset (see the gate above — only the interval
     * skips clean sessions), and that asset holds byte-identical redactions, so
     * its relationship to the burn-in is exactly the one the last save recorded.
     * Marking it unsent would report a file stale that nobody had touched.
     *
     * VE-25718: the test is `hasUnsavedWork`, NOT `status === 'dirty'`. Since
     * a failed save is now retried by the interval, an `error` session reaches
     * here carrying real unsaved edits — and `SAVE_FAILURE` preserves
     * `lastSavedBurnInVersion`, so a literal `'dirty'` test would take the
     * clean-session branch and stamp those new redactions with the PREVIOUS
     * burn-in version. Staleness would then read the output as current and
     * clear the banner on a file that is genuinely behind.
     */
    const expectedRedactedVersion =
      manual?.payload?.expectedRedactedVersion ??
      (hasUnsavedWork(session.save)
        ? undefined
        : (session.save.lastSavedBurnInVersion ?? undefined));
    yield* persistSessionSnapshot(session, {
      requestId,
      expectedRedactedVersion,
    });
  }
}

/**
 * VE-24947 — save every open document that has unsaved work.
 *
 * The one on screen AND every session suspended by a switch. A reviewer
 * working through a request accumulates unsaved work in files they can no
 * longer see, and until now the only way to save those was to navigate back to
 * each one.
 *
 * SEQUENTIAL, not parallel. Each save is a GraphQL round trip plus an asset
 * write, and `persistSessionSnapshot` dispatches SAVE_REQUEST / SAVE_SUCCESS
 * per document — firing them concurrently would interleave those into a save
 * state that reports on whichever finished last. Slower, legible.
 *
 * Failures do not stop the pass: one document failing is no reason to abandon
 * the other four. They are collected and reported on SAVE_ALL_SETTLED, and the
 * failed sessions keep `error` status, which `hasUnsavedWork` still counts as
 * unsaved — so the panel's markers and the leave guard stay honest by
 * themselves.
 */
export function* saveAllDocumentsSaga() {
  yield* takeLatest(SAVE_ALL_DOCUMENTS, function* handleSaveAll() {
    const active: SessionState | null = yield* select(selectSession);
    const suspended: Record<string, SessionState> = yield* select(
      selectSuspendedSessions
    );

    const targets: SessionState[] = [
      ...(active && hasUnsavedWork(active.save) ? [active] : []),
      ...Object.values(suspended).filter((s) => hasUnsavedWork(s.save)),
    ];

    const failed: TDOId[] = [];
    for (const session of targets) {
      // Mirrors the manual-save branch above: a session with unsaved work must
      // NOT carry the previous burn-in version forward, or staleness reads the
      // output as current for redactions that never reached it.
      const result = yield* persistSessionSnapshot(session, {
        expectedRedactedVersion: undefined,
      });
      if (!result?.ok) {
        failed.push(session.tdoId);
      }
    }

    yield* put(SAVE_ALL_SETTLED({ failed }));
  });
}
